import { Injectable } from '@angular/core'
import { BuilderService } from '../../builder-services/builder.service'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { HelpersService } from '../../helpers.service'
import { SettingsApiService } from 'src/app/builder-services/api/settings.api.service'
import { UsersApiService } from 'src/app/builder-services/api/users.api.service'
import { Observable } from 'rxjs'
import { OrdersService } from 'src/app/e-commerce/orders/orders.service'
import { AppSettingsService } from 'src/app/settings/services/app-settings.service'

export interface TypeObject {
  type: string
  message: string // Ensure message is always present, but it can be an empty string initially.
}

@Injectable({
  providedIn: 'root'
})
export class PushNotificationsService {
  // nameOptions: string[] = ['User Action', 'Order Status'];
  nameOptions: string[] = ['Order Status']
  project_id = this.builderService.getSelectedProject()

  typeOptions = ['Order Received', 'Processing', 'Confirmed', 'Completed', 'Cancelled','Refunded']

  defaultEvents = [
    {
      id: 1,
      date: this.getFormattedCurrentDate(),
      name: 'Order Status',
      types: [...this.typeOptions],
      selectedType: 'Order Received',
      selectedMessage: 'order received successfully :)'
    }
  ]

  userList: any = []
  deviceIDs: any = []

  private builderUrl: string
  options = {
    headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
  }
  token: any

  constructor(private builderService: BuilderService, private helper: HelpersService, private http: HttpClient, private settingsApi: SettingsApiService,
    private usersApi: UsersApiService, private ordersService: OrdersService, private appSettingsService: AppSettingsService) {
    this.builderUrl = this.helper.getBuilderUrl()
  }
  /**
   * Get all the users of the Customer website
   * @param id the Project ID
   */
  getUsers = (id: any) => {
    if (this.builderService.standalone) {
      return new Observable<any>((observer) => {
        this.usersApi.getUsers(String(this.builderService.selectedProject)).then((response: any) => {
          observer.next(response) // Notify the observer that the operation is complete
          observer.complete()
        })
      })
    } else {
      this.token = this.builderService.getToken()
      let postData = new URLSearchParams()
      postData.set('token', this.token)
      postData.set('projectId', id)
      return this.http.post(this.builderUrl + 'get-users', postData.toString(), this.options)
    }
  }
  /**
   *
   * @param title The title of the Notification
   * @param message The Message of the Notification
   * @param users Comma Separated List of the users
   * @param route The route to be executed when the Notification is received
   * @param specificRoute The specific route to be executed when the Notification is received
   * @returns
   * Send Push Notification
   */
  sendAndroidMessage = (databaseURL: string, file: any, senderId: any, title: string, message: string, users: any, route: any = '', specificRoute: any = '') => {
    let routing
    let productId
    this.deviceIDs = []
    for (let i of users) {
      this.deviceIDs.push(i.device_id)
    }
    if (specificRoute) {
      if (route === 'products') {
        routing = '/product-single'
        productId = specificRoute
      }
    } else {
      routing = route
    }
    let data = {
      page: routing,
      productId: productId,
      specificRoute: specificRoute
    }
    const formData = new FormData()
    formData.append('databaseURL', databaseURL)
    formData.append('serviceAccountFile', file) // File field name must match backend
    formData.append('deviceTokens', this.deviceIDs)
    formData.append('title', title)
    formData.append('body', message)
    formData.append('data', JSON.stringify(data))
    formData.append('senderId', senderId)

    return this.http.post('https://app.stacksmarket.co/send-notification', formData)
  }

  sendIOSMessage = (title: string, message: string, users: any, projectId: any, route: any='', specificRoute: any='') => {
    this.deviceIDs = []
    for (let i of users) {
      this.deviceIDs.push(i.device_id)
    }
    // Determine the route for navigation
    let finalRoute = route;
    if (specificRoute && route === 'products') {
      finalRoute = `/product-single/${specificRoute}`; // Example: /product-single/123
    }

    let postData = new URLSearchParams()
    postData.set('projectId', projectId)
    postData.set('token', this.token)
    postData.set('deviceTokens', this.deviceIDs)
    postData.set('title', title)
    postData.set('message', message)
    postData.set('route', finalRoute); // Send route directly as expected by backend
    postData.set('specificRoute', specificRoute); // Send route directly as expected by backend

    return this.http.post(this.builderUrl + 'send-ios-notifications', postData.toString(), this.options)
  }

  /**
   * Validates that the fields of the push notifications are saved correctly
   * @param id project id
   */
  validatePN = (id: any) => {
    this.token = this.builderService.getToken()
    let postData = new URLSearchParams()
    postData.set('projectId', id)
    postData.set('token', this.token)

    return this.http.post(this.builderUrl + 'validate-notifications', postData.toString(), this.options)
  }

 /**
   * Automates the process of sending push notifications based on the given order status and user ID.
   * @param status - The order status for which notifications should be sent.
   * @param userId - The ID of the user for whom notifications should be sent.
   * @returns {void}
   * @remarks
   * This function checks if the given order status is available in the predefined type options.
   * If the status is not available, the function exits early.
   * It then fetches the automation data from the database and iterates through it.
   * If a matching order status is found, it fetches the users once for the specific user ID.
   * If the user is found, it sends a push notification using the `sendMessage` function.
   * The function also retrieves the application settings and sends the notification using the appropriate settings.
   * If the user is not found, it logs a message indicating the user ID not found.
   */
 automate(status: string, userId: string) {
  if (!this.checkAvailability(status)) {
    return // Exit early if availability check fails
  }
  this.settingsApi.getSettingsByType(String(this.project_id), 'automation').then((settings: any) => {
    const raw = settings['events']
    const data: any[] = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : []
    if (!Array.isArray(data)) return;
    for (let i = 0; i < data.length; i++) {
      if (data[i].selectedType === status) {
        // Fetch users once for the specific userId
        this.getUsers(this.project_id).subscribe((users: any) => {
          if (users[userId]) {
            // Directly check if the user exists
            const user = users[userId]
            // Use an array with just this user
            const targetUsers = [user]
            this.appSettingsService.getApplicationSettings(this.project_id).subscribe((settings: any) => {
              if (settings && (settings.android_sender_id || settings.databaseURL)) {
                this.settingsApi.getSettingsByType(String(this.project_id), 'application_settings').then((response: any) => {
                  const serviceAccountFile = this.appSettingsService.convertBase64ToFile(response['serviceAccBase64'], 'serviceAccount.json')
                  let users_android = targetUsers.filter((user:any) => user.device_type === 'android');
                  let users_ios  = targetUsers.filter((user:any) => user.device_type === 'ios');
                  
                  if(users_android.length>0){
                  this.sendAndroidMessage(settings.databaseURL, serviceAccountFile, settings.android_sender_id, 'Order Update', data[i].selectedMessage,users_android ).subscribe(
                    (response: any) => {
                      this.builderService.showUpdateProgress = false
                    },
                    (err) => {
                      console.log('error', err)
                    }
                  )
                }
                if(users_ios.length>0){
                  this.sendIOSMessage('Order Update',data[i].selectedMessage, users_ios, String(localStorage.getItem('selectedProject'))).subscribe(
                    (response: any) => {
                      this.builderService.showUpdateProgress = false
                    },
                    (err) => {
                      console.log('error', err)
                    }
                  )
                }
                })
              }
            })
          } else {
            console.log('User not found for ID:', userId)
          }
        })
      }
    }
  })
}

  /**
   * Checks if a given order status name is available in the predefined type options.
   * @param name - The name of the order status to check for availability.
   * @returns A boolean indicating whether the order status name is available (`true`) or not (`false`).
   */
  checkAvailability(name: string) {
    for (let i of this.typeOptions) {
      if (i === name) {
        return true
      }
    }
    return false
  }

  getFormattedCurrentDate(): string {
    const today = new Date()
    const month = (today.getMonth() + 1).toString().padStart(2, '0') // Add 1 because months are 0-indexed
    const day = today.getDate().toString().padStart(2, '0')
    const year = today.getFullYear()
    return `${month}/${day}/${year}`
  }
}
